import { useState, useRef, useEffect, useMemo } from "react";
import type {
  DeploymentDataType,
  HardwareItemType,
  DeploymentSubscriptionAssignDataType,
} from "@/schemas/index";
import { useQueueProcess } from "@/app/hook/use-queue-process";
import { useNotifications } from "@/app/hook/use-notifications";
import { useDeploymentService } from "@/services";
import { ModalHardwareInstallProps } from "../types/index";
import { useModalNavigate } from "@/app/hook/use-modal-navigate";
import { useForm } from "@/base/utils/index";

type ModalHardwareInstallPageIdType =
  | "loading"
  | "business-unit-select"
  | "business-unit-create"
  | "cr-select"
  | "finish";

export function useModalHardwareInstallHook({
  isOpenDrawer,
  hardwareItem,
  close,
  open,
  onFinish,
}: ModalHardwareInstallProps["controller"]) {
  const [form] = useForm();

  const {
    currentPageId,
    fetchBackDrawerPage,
    fetchChangeDrawerPage,
    fetchResetDrawerPageHistory,
  } = useModalNavigate<ModalHardwareInstallPageIdType>(
    form,
    "business-unit-select",
  );

  const currentPromiseFetchInit = useRef<any>();

  const [deploymentData, setDeploymentData] = useState<DeploymentDataType>();

  const notifications = useNotifications("modal");

  const processKey = useMemo(
    () => `hardware-install-${hardwareItem?.Id}`,
    [hardwareItem?.Id],
  );
  const { addQueueProcess, currentProcess } =
    useQueueProcess<DeploymentSubscriptionAssignDataType>(processKey);

  const { fetchDeploymentSummary, fetchSubscriptionAssign } =
    useDeploymentService();

  const fetchDeploymentData = (hardwareItem: HardwareItemType) => {
    if (currentPromiseFetchInit.current) {
      return currentPromiseFetchInit.current;
    }

    if (!hardwareItem?.ProductId) {
      throw new Error("ProductId is required");
    }

    fetchChangeDrawerPage("loading");
    currentPromiseFetchInit.current = fetchDeploymentSummary(
      hardwareItem.ProductId,
    );

    return currentPromiseFetchInit.current
      .then((deploymentData: DeploymentDataType) => {
        setDeploymentData(deploymentData);
      })
      .finally(() => {
        fetchChangeDrawerPage("business-unit-select");
        currentPromiseFetchInit.current = null;
      });
  };

  const fetchSubmitHardwareInstall = (
    data: DeploymentSubscriptionAssignDataType,
  ) => {
    addQueueProcess({
      key: processKey,
      data: data,
      promise: new Promise((resolve, reject) => {
        fetchChangeDrawerPage("finish");
        return fetchSubscriptionAssign(data)
          .then(() => {
            onFinish?.();
            resolve(void 0);
          })
          .catch((error) => {
            notifications.show(error as Error);
            reject(error as Error);
          });
      }),
    });
  };

  useEffect(() => {
    if (hardwareItem) {
      fetchDeploymentData(hardwareItem).catch(notifications.show);
    }
  }, [hardwareItem]);

  useEffect(() => {
    if (isOpenDrawer) {
      fetchResetDrawerPageHistory();
    }
  }, [isOpenDrawer]);

  return {
    form,
    hardwareItem,
    deploymentData,
    currentPageId,
    currentProcess,
    isOpenDrawer,

    close,
    fetchChangeDrawerPage,
    fetchBackDrawerPage,
    open,
    fetchSubmitHardwareInstall,
  };
}
